import { api } from "@/lib/http";

/*
 * Embedding model governance: the organization's single embedding record --
 * a catalog model plus the org's own provider key. The backend never returns
 * the stored key, only `api_key_hint`.
 */

export const EMBEDDING_HISTORY_ACTIONS = [
  "created",
  "enabled",
  "disabled",
  "model_changed",
  "api_key_rotated",
  "details_updated",
  "deleted",
] as const;
export type EmbeddingHistoryAction = (typeof EMBEDDING_HISTORY_ACTIONS)[number];

export type EmbeddingConfigStatus = "enabled" | "disabled" | "unavailable";
export type EmbeddingUnavailableReason = "MODEL_DISABLED" | "MODEL_REMOVED";

/** A catalog entry the organization can pick from. */
export type EmbeddingModel = {
  id: string;
  provider: string;
  name: string;
  qualified_name: string;
  description: string | null;
};

export type EmbeddingModelRef = {
  id: string;
  provider: string;
  name: string;
  qualified_name: string;
};

export type UserRef = {
  id: string;
  full_name: string;
};

/** The organization's embedding record. The key itself is never returned. */
export type EmbeddingConfig = {
  id: string;
  name: string;
  description: string | null;
  embedding_model: EmbeddingModelRef;
  is_enabled: boolean;
  status: EmbeddingConfigStatus;
  unavailable_reason: EmbeddingUnavailableReason | null;
  /** "••••" plus the key's last four characters. */
  api_key_hint: string;
  last_validated_at: string;
  version: number;
  created_by: UserRef | null;
  updated_by: UserRef | null;
  created_at: string;
  updated_at: string | null;
};

export type EmbeddingConfigCreate = {
  embedding_model_id: string;
  name: string;
  api_key: string;
  description?: string | null;
};

export type EmbeddingConfigUpdate = {
  embedding_model_id?: string;
  /** Leave out to keep the current key. */
  api_key?: string;
  name?: string;
  /** Leave out for no change; send "" to clear. */
  description?: string | null;
  is_enabled?: boolean;
  /** The `version` from the last GET; the backend rejects stale writes. */
  expected_version: number;
  confirm_reindex?: boolean;
};

export type EmbeddingChangeSide = {
  embedding_model_id?: string | null;
  provider?: string | null;
  model_name?: string | null;
  qualified_name?: string | null;
  credential_name?: string | null;
  is_enabled?: boolean | null;
};

export type EmbeddingHistoryEntry = {
  version: number;
  action: EmbeddingHistoryAction | (string & {});
  changed_at: string;
  changed_by: { id: string; name: string | null; email: string | null };
  from: EmbeddingChangeSide | null;
  to: EmbeddingChangeSide | null;
  api_key_rotated: boolean;
  reindex_confirmed: boolean;
};

export type EmbeddingHistoryList = {
  items: Array<EmbeddingHistoryEntry>;
  count: number;
  next_page: string | null;
  previous_page: string | null;
};

function listModels() {
  return api.raw.get("api/v1/embedding/models").json<Array<EmbeddingModel>>();
}

/** Throws a ky HTTPError with status 404 when nothing is configured yet. */
function getConfig() {
  return api.raw.get("api/v1/embedding/api-key").json<EmbeddingConfig>();
}

// create / update run a live test call against the provider before saving,
// so they get more than ky's default 10 s
function createConfig(body: EmbeddingConfigCreate) {
  return api.raw
    .post("api/v1/embedding/api-key", { json: body, timeout: 30_000 })
    .json<EmbeddingConfig>();
}

function updateConfig(body: EmbeddingConfigUpdate) {
  return api.raw
    .patch("api/v1/embedding/api-key", { json: body, timeout: 30_000 })
    .json<EmbeddingConfig>();
}

function deleteConfig(confirmReindex: boolean) {
  return api.raw.delete("api/v1/embedding/api-key", {
    searchParams: { confirm_reindex: String(confirmReindex) },
  });
}

function getHistory(page: number, perPage = 10) {
  return api.raw
    .get("api/v1/embedding/api-key/history", {
      searchParams: { page, per_page: perPage },
    })
    .json<EmbeddingHistoryList>();
}

export default {
  listModels,
  getConfig,
  createConfig,
  updateConfig,
  deleteConfig,
  getHistory,
};
